import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity } from 'react-native';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { Toast } from '../components/common/Toast';
import { WebPageShell } from '../components/common/WebPageShell';
import { RequestDetailView } from '../components/documents/RequestDetailView';
import { EmptyState } from '../components/documents/DocumentUi';
import { RootStackParamList } from '../types';

/** One document request: progress, corrections, answers, download (web). */
export function DocumentRequestDetailScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<RouteProp<RootStackParamList, 'DocumentRequestDetail'>>();
  const requestId = route.params?.requestId;
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  const goBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('Documents', { tab: 'requests' });
  };

  return (
    <WebPageShell title="Documents" maxWidth={820}>
      <TouchableOpacity
        style={styles.backRow}
        onPress={goBack}
        accessibilityRole="link"
        accessibilityLabel="Back to documents">
        <FeatherIcon name="arrow-left" size={16} color={colors.primary} />
        <Text style={styles.backText}>Back to documents</Text>
      </TouchableOpacity>

      {requestId ? (
        <RequestDetailView requestId={requestId} scrollable={false} notify={notify} />
      ) : (
        <EmptyState icon="file" title="Request not found" actionLabel="Back to documents" onAction={goBack} />
      )}

      <Toast
        visible={!!toast}
        message={toast?.message ?? ''}
        type={toast?.type ?? 'success'}
        onDismiss={() => setToast(null)}
      />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    backRow: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: 8, marginBottom: 16, paddingVertical: 4 },
    backText: { fontSize: 14, fontWeight: '600', color: colors.primary },
  });
